/**
 * Error Page for Admin Console
 * 
 * Displays authentication errors from Kratos flows.
 */

import { useEffect, useState } from 'react';
import { FlowError } from '@ory/client';
import { identityClient } from '@/lib/auth';
import { Loader2, X } from 'lucide-react';
import { cn } from '@/lib/utils';

// =============================================================================
// Error Page Component
// =============================================================================

export function ErrorPage() {
  const [error, setError] = useState<FlowError | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchError = async () => {
      try {
        const params = new URLSearchParams(window.location.search);
        const errorId = params.get('id');

        if (errorId) {
          const { data } = await identityClient.getFlowError({ id: errorId });
          setError(data);
        }
      } catch (err) {
        console.error('Failed to fetch error details:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchError();
  }, []);

  // Get error message
  const getErrorMessage = (): { title: string; description: string } => {
    if (!error?.error) {
      return {
        title: 'Authentication Error',
        description: 'An unexpected error occurred during authentication.',
      };
    }

    const errorObj = error.error as { code?: number; message?: string; reason?: string };
    const { code, message, reason } = errorObj;

    // Handle common error codes
    switch (code) {
      case 400:
        return {
          title: 'Invalid Request',
          description: message || 'The authentication request was invalid.',
        };
      case 401:
        return {
          title: 'Unauthorized',
          description: message || 'Your session has expired. Please log in again.',
        };
      case 403:
        return {
          title: 'Access Denied',
          description: message || 'You do not have permission to access this resource.',
        };
      case 404:
        return {
          title: 'Not Found',
          description: message || 'The requested resource was not found.',
        };
      case 410:
        return {
          title: 'Session Expired',
          description: 'This authentication flow has expired. Please start again.',
        };
      case 429:
        return {
          title: 'Too Many Requests',
          description: 'You have made too many requests. Please wait a moment and try again.',
        };
      case 500:
        return {
          title: 'Server Error',
          description: 'An internal error occurred. Please try again later.',
        };
      default:
        return {
          title: reason || 'Authentication Error',
          description: message || 'An unexpected error occurred.',
        };
    }
  };

  const { title, description } = getErrorMessage();

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <Loader2 className="w-12 h-12 text-blue-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 px-4">
      <div className="w-full max-w-md text-center">
        {/* Error icon */}
        <div className="flex justify-center mb-6">
          <div className="w-20 h-20 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center">
            <X className="w-10 h-10 text-red-600 dark:text-red-400" />
          </div>
        </div>

        {/* Error message */}
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
          {title}
        </h1>
        <p className="text-gray-600 dark:text-gray-400 mb-8">
          {description}
        </p>

        {/* Debug info (only in development) */}
        {import.meta.env.DEV && error?.error && (
          <div className="mb-8 text-left">
            <details className="bg-gray-100 dark:bg-gray-800 rounded-lg p-4">
              <summary className="text-sm font-medium text-gray-700 dark:text-gray-300 cursor-pointer">
                Technical Details
              </summary>
              <pre className="mt-2 text-xs text-gray-600 dark:text-gray-400 overflow-auto">
                {JSON.stringify(error.error, null, 2)}
              </pre>
            </details>
          </div>
        )}

        {/* Action buttons */}
        <div className="space-y-3">
          <a
            href="/auth/login"
            className={cn(
              'block w-full py-3 px-4 rounded-lg font-medium transition-colors',
              'bg-blue-600 text-white hover:bg-blue-700',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500'
            )}
          >
            Back to Login
          </a>
          <a
            href="/"
            className={cn(
              'block w-full py-3 px-4 rounded-lg font-medium transition-colors',
              'border border-gray-300 dark:border-gray-600',
              'bg-white dark:bg-gray-800',
              'text-gray-700 dark:text-gray-200',
              'hover:bg-gray-50 dark:hover:bg-gray-700',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500'
            )}
          >
            Go to Homepage
          </a>
        </div>

        {/* Help text */}
        <p className="mt-8 text-sm text-gray-500 dark:text-gray-400">
          If you continue to experience issues, please contact your administrator.
        </p>
      </div>
    </div>
  );
}

export default ErrorPage;
